import BellIcon from "@shared/svg/bell.svg?react";
import CalendarIcon from "@shared/svg/calendar-blank.svg?react";
import React from "react";

interface WorkOrderCardDueTagProps {
  /** Days remaining (positive = days left, negative = overdue) */
  daysRemaining?: number;
  /** Is card hovered */
  isHovered?: boolean;
}

/**
 * WorkOrderCardDueTag
 *
 * Shows days remaining or overdue status for a work order.
 */
export const WorkOrderCardDueTag: React.FC<WorkOrderCardDueTagProps> = ({
  daysRemaining,
  isHovered = false,
}) => {
  if (daysRemaining === undefined) return null;

  const isOverdue = daysRemaining < 0;
  const displayDays = Math.abs(daysRemaining);
  const text = isOverdue
    ? `${displayDays} days overdue`
    : `${displayDays} days left`;

  // Overdue: red theme, otherwise secondary theme
  const bgClass = isHovered
    ? isOverdue
      ? "bg-danger"
      : "bg-secondary"
    : isOverdue
    ? "bg-danger-lightest"
    : "bg-accent";

  const textClass = isHovered
    ? "text-inverse"
    : isOverdue
    ? "text-danger"
    : "text-secondary";

  return (
    <div
      className={`flex items-center gap-2xs px-xs py-2xs rounded ${bgClass}`}
    >
      <BellIcon className={`w-md h-md ${textClass}`} />
      <span className={`text-xs font-bold ${textClass}`}>{text}</span>
    </div>
  );
};

interface WorkOrderCardScheduleTagProps {
  /** Scheduled date string */
  date?: string;
  /** Is card hovered */
  isHovered?: boolean;
}

/**
 * Format date to DD/MM/YYYY
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * WorkOrderCardScheduleTag
 *
 * Shows scheduled date for a work order.
 */
export const WorkOrderCardScheduleTag: React.FC<
  WorkOrderCardScheduleTagProps
> = ({ date, isHovered = false }) => {
  if (!date) return null;

  const bgClass = isHovered ? "bg-warning" : "bg-warning-lightest";
  const textClass = isHovered ? "text-inverse" : "text-warning";

  return (
    <div
      className={`flex items-center gap-2xs px-xs py-2xs rounded ${bgClass}`}
    >
      <CalendarIcon className={`w-md h-md ${textClass}`} />
      <span className={`text-xs font-semibold uppercase ${textClass}`}>
        {formatDate(date)}
      </span>
    </div>
  );
};

interface WorkOrderCardStatusTagProps {
  /** Status label */
  label: string;
  /** Status color (hex) */
  color?: string;
  /** Is card hovered */
  isHovered?: boolean;
}

/**
 * WorkOrderCardStatusTag
 *
 * Shows the status badge for a work order.
 */
export const WorkOrderCardStatusTag: React.FC<WorkOrderCardStatusTagProps> = ({
  label,
  color = "#EE0064",
  isHovered = false,
}) => {
  // Use solid background on hover
  const bgStyle = isHovered
    ? { backgroundColor: color }
    : { backgroundColor: `${color}20` }; // 20 = ~12% opacity

  const textStyle = isHovered ? { color: "#FFFFFF" } : { color };

  return (
    <div
      className="flex items-center gap-2xs px-xs py-2xs rounded"
      style={bgStyle}
    >
      <span className="text-xs font-bold uppercase" style={textStyle}>
        {label}
      </span>
    </div>
  );
};
